/**
 * Chart chrome for recharts, in shadcn/ui's tooltip shape: a small popover
 * card with a colour swatch per series, the series name in muted ink, and the
 * value right-aligned in tabular figures. Adapted to this workspace's tokens
 * (popover/border/foreground) so every theme paints its own tooltips.
 *
 * Only the tooltip is provided — this app's charts are simple enough that the
 * rest of shadcn's chart config plumbing would be scaffolding with no reader.
 *
 * @module components/ui/chart
 */
import type { TooltipContentProps } from "recharts";

import { cn } from "~/lib/utils";

type TooltipItem = {
  readonly name?: string | number;
  readonly value?: string | number | ReadonlyArray<string | number>;
  readonly color?: string;
  readonly payload?: { fill?: string } & Record<string, unknown>;
};

export function ChartTooltipContent({
  active,
  payload,
  label,
  hideLabel = false,
  className,
}: Partial<
  Pick<TooltipContentProps<number | string, string>, "active" | "payload" | "label">
> & {
  readonly hideLabel?: boolean;
  readonly className?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      className={cn(
        "min-w-32 rounded-lg border border-border/60 bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md",
        className,
      )}
    >
      {!hideLabel && label !== undefined && label !== "" && (
        <div className="mb-1.5 font-medium">{String(label)}</div>
      )}
      <div className="grid gap-1.5">
        {(payload as ReadonlyArray<TooltipItem>).map((item, index) => (
          <div key={`${String(item.name)}-${index}`} className="flex w-full items-center gap-2">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ background: item.payload?.fill ?? item.color ?? "currentColor" }}
            />
            <span className="text-muted-foreground">{String(item.name ?? "")}</span>
            <span className="ms-auto font-medium tabular-nums text-foreground">
              {Array.isArray(item.value) ? item.value.join("–") : String(item.value ?? "")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
